import './style.css';
import { showLoadingScreen, resolveLoad } from './components/LoadingScreen.js';
import { renderHeroSection } from './components/HeroSection.js';
import { renderMenuIcon } from './components/MenuIcon.js';
import { renderAboutSection } from './components/AboutSection.js';
import { renderScrollDots } from './components/ScrollDots.js';
import { openReader } from './components/Reader.js';
import { openStoryPicker } from './components/StoryPicker.js';
import { novels } from './data/novels.js';
import { novelChapters } from './data/novel-chapters.js';
import { openNovelPrologue, closeChapterReaderInstant } from './components/ChapterReader.js';
import { stories } from './data/stories.js';
import { createEmailForm } from './components/EmailForm.js';
import { renderSocialIcons } from './components/SocialIcons.js';

/*
  ORCHESTRATOR PATTERN
  ---------------------
  Vertical scroll-snap container, one full-bleed hero section per work.
  Hard snap between sections - no in-between scroll state.

  Event contract:
    'cover:clicked'    -> fired when a hero's CTA is clicked
    'email:submitted'  -> fired by EmailForm.js on successful submit
    'reader:opened'    -> fired when a story's full text is shown
*/

showLoadingScreen();

const appRoot = document.getElementById('app-root');

const scrollContainer = document.createElement('div');
scrollContainer.className = 'h-[100dvh] w-full overflow-y-scroll snap-y snap-mandatory';

const story = stories[0];
const novel = novels[0];

const shortsSection = renderHeroSection({
  image: story.heroImage || story.coverImage,
  label: "Myth's Shorts",
  title: 'Crow\u2019s Tales',
  subtitle: 'Stories from Nine Deaths',
  hook: 'Remember, you volunteered.',
  ctaLabel: 'Enter',
  titleStyle: 'shorts',
  showDownArrow: true,
  onEnter: () => {
    window.dispatchEvent(new CustomEvent('cover:clicked', { detail: { id: story.id, type: 'story' } }));
    openStoryPicker(stories);
  },
});
shortsSection.id = 'section-0';

const novelsSection = renderHeroSection({
  image: novel.coverImage,
  label: "Myth's Novels",
  title: novel.title,
  titleAccent: novel.titleAccent,
  subtitle: novel.subtitle,
  hook: novel.blurb,
  ctaLabel: novel.status === 'coming-soon' ? 'Read Prologue \u2014' : 'Enter',
  showDownArrow: false,
  ...(novel.heroConfig || {}),
  onEnter: () => {
    window.dispatchEvent(new CustomEvent('cover:clicked', { detail: { id: novel.id, type: 'novel' } }));
    const chapters = novelChapters[novel.id];
    if (chapters && chapters.length > 0) {
      openNovelPrologue(novel.id, [
        { title: 'Chapter I \u2014 The Pious, Before the Ash', label: 'NEXT CHAPTER \u2022 14 MIN' },
        {
          title: stories[0].title,
          label: 'STORY I \u2022 4 MIN READ',
          onClick: () => {
            closeChapterReaderInstant();
            import('./components/StoryPicker.js').then((mod) => {
              mod.openStoryPicker(stories);
            });
          },
        },
      ]);
    } else if (novel.status === 'coming-soon') {
      const existingForm = novelsSection.querySelector('[data-role="inline-email-form"]');
      if (!existingForm) {
        const formWrap = document.createElement('div');
        formWrap.dataset.role = 'inline-email-form';
        formWrap.className = 'relative z-10 px-6';
        formWrap.appendChild(createEmailForm({
          source: novel.id,
          message: `"${novel.title}" isn't out yet. Get notified the day it drops.`,
        }));
        novelsSection.appendChild(formWrap);
      }
    } else if (novel.link) {
      window.location.href = novel.link;
    }
  },
});
novelsSection.id = 'section-1';

const aboutSection = renderAboutSection();
aboutSection.id = 'section-2';

scrollContainer.append(shortsSection, novelsSection, aboutSection);
appRoot.appendChild(scrollContainer);
appRoot.appendChild(renderMenuIcon());
appRoot.appendChild(renderScrollDots(['section-0', 'section-1', 'section-2']));

// Social/contact icon row, fixed top-left, present across every
// landing page (including About) - replaces the old in-panel FB/IG/
// Pinterest row that used to live only inside AboutSection.js.
const socialRow = renderSocialIcons({ variant: 'landing' });
appRoot.appendChild(socialRow);

// Persistent SUPPORT link, fixed bottom-right - a single clickable
// button to Patreon, distinct from the social icon row above.
// Placeholder href until director provides the real Patreon URL.
const supportLink = document.createElement('a');
supportLink.href = 'https://ko-fi.com/stygianmyth';
supportLink.target = '_blank';
supportLink.rel = 'noopener noreferrer';
supportLink.className = 'site-support-link';
supportLink.textContent = 'Support';
appRoot.appendChild(supportLink);

// Gate the loading screen's exit on real readiness: the shorts hero's
// background image actually loaded, plus web fonts finished loading.
// resolveLoad() only sets a flag - LoadingScreen.js lets the current
// flip cycle finish naturally before fading out, so this doesn't need
// to be perfectly timed to the frame.
(function gateLoadingScreen() {
  const heroImgUrl = story.heroImage || story.coverImage;
  const imgReady = new Promise((resolve) => {
    if (!heroImgUrl) {
      resolve();
      return;
    }
    const probe = new Image();
    probe.onload = () => resolve();
    probe.onerror = () => resolve(); // don't hang the loading screen forever on a bad URL
    probe.src = heroImgUrl;
  });

  const fontsReady = document.fonts && document.fonts.ready
    ? document.fonts.ready
    : Promise.resolve();

  Promise.all([imgReady, fontsReady]).then(() => {
    resolveLoad();
  });
})();

const DEBUG = true;
if (DEBUG) {
  ['cover:clicked', 'email:submitted', 'reader:opened'].forEach((evt) => {
    window.addEventListener(evt, (e) => {
      console.log(`[event] ${evt}`, e.detail ?? '');
    });
  });
}
